import { StateCreator } from 'zustand';
import { CedarStore } from './types';
import React from 'react';

// Define the modal component interface
export type ModalComponentType = React.ComponentType<{
	onClose: () => void;
	isOpen: boolean;
	title?: string;
	className?: string;
}>;

// Define the flattened Cedar Chat configuration interface
export interface CedarChatConfig {
	// Modal configuration
	mode?: 'embedded' | 'floating' | 'custom_container' | 'bar' | 'sidepanel';
	position?: 'bottom-right' | 'bottom-left' | 'bottom-center';
	/** Title displayed in chat components (e.g. headers, modals). Defaults to "Cedar Copilot" */
	title?: string;
	side?: 'left' | 'right';
	// CedarBar / container style
	style: 'glassy' | 'grainy' | '3d';
	// Route configuration - array of routes where cedar chat should be shown
	routes?: string[];
}

// Define the slice for UI
export interface UISlice {
	showButton: boolean;
	showCedarChat: boolean;
	isCedarChatEnabled: boolean;
	modalComponent: ModalComponentType | null;
	cedarChatConfig: CedarChatConfig;
	variables: Record<string, React.ReactNode>;
	flags: Record<string, string | boolean | object>;
	setShowButton: (show: boolean) => void;
	setShowCedarChat: (show: boolean) => void;
	setIsCedarChatEnabled: (enabled: boolean) => void;
	setModalComponent: (component: ModalComponentType | null) => void;
	setCedarChatConfig: (config: Partial<CedarChatConfig>) => void;
	setFlag: (key: string, value: string | boolean | object) => void;
	setFlags: (flags: Record<string, string | boolean | object>) => void;
	setVariable: (key: string, value: React.ReactNode) => void;
	setVariables: (variables: Record<string, React.ReactNode>) => void;
	removeVariable: (key: string) => void;
	removeFlag: (key: string) => void;
	clearVariables: () => void;
	clearFlags: () => void;
	toggleButton: () => void;
	toggleCedarChat: () => void;
	toggleIsCedarChatEnabled: () => void;
}

// Create the slice
export const createUISlice: StateCreator<CedarStore, [], [], UISlice> = (
	set
) => ({
	// Default state
	showButton: true,
	showCedarChat: false,
	isCedarChatEnabled: false,
	modalComponent: null,
	cedarChatConfig: {
		mode: 'floating',
		position: 'bottom-right',
		side: 'right',
		style: '3d',
		title: 'Cedar Copilot',
	},
	variables: {},
	flags: {},

	// Actions
	setShowButton: (show: boolean) => set({ showButton: show }),
	setShowCedarChat: (show: boolean) => set({ showCedarChat: show }),
	setIsCedarChatEnabled: (enabled: boolean) =>
		set({ isCedarChatEnabled: enabled }),
	setModalComponent: (component: ModalComponentType | null) =>
		set({ modalComponent: component }),
	setCedarChatConfig: (config: Partial<CedarChatConfig>) =>
		set((state) => ({
			cedarChatConfig: {
				...state.cedarChatConfig,
				...config,
			},
		})),
	setVariable: (key: string, value: React.ReactNode) =>
		set((state) => ({
			variables: { ...state.variables, [key]: value },
		})),
	setVariables: (newVariables: Record<string, React.ReactNode>) => {
		set((state) => ({ variables: { ...state.variables, ...newVariables } }));
	},
	setFlag: (key: string, value: string | boolean | object) =>
		set((state) => ({
			flags: { ...state.flags, [key]: value },
		})),
	setFlags: (newFlags: Record<string, string | boolean | object>) => {
		set((state) => ({ flags: { ...state.flags, ...newFlags } }));
	},
	removeVariable: (key: string) =>
		set((state) => {
			const newVariables = { ...state.variables };
			delete newVariables[key];
			return { variables: newVariables };
		}),
	removeFlag: (key: string) =>
		set((state) => {
			const newFlags = { ...state.flags };
			delete newFlags[key];
			return { flags: newFlags };
		}),
	clearVariables: () => set({ variables: {} }),
	clearFlags: () => set({ flags: {} }),

	toggleButton: () =>
		set((state: CedarStore) => ({
			showButton: !state.showButton,
		})),

	toggleCedarChat: () =>
		set((state: CedarStore) => ({
			showCedarChat: !state.showCedarChat,
		})),

	toggleIsCedarChatEnabled: () =>
		set((state: CedarStore) => ({
			isCedarChatEnabled: !state.isCedarChatEnabled,
		})),
});
